// Multi-select assignee filter for the dashboard.
//
// Built to scale: trigger is a single chip-button regardless of how many
// teammates exist; the picker is a dropdown with search (visible when 8+
// names) + checkbox rows. Counts shown next to each row reflect the current
// group + other active filters so the numbers always match what you'd see.
//
// Why not chip-row? With ~20 teammates the chip row blew past the filter
// strip. A single dropdown trigger with a smart label ("All" / "Rex" /
// "Rex, Liz" / "3 selected") keeps the bar at one line forever.

import { useMemo, useState } from 'react';
import { Check, ChevronDown, Search, Users, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { UserAvatar } from '@/components/UserAvatar';
import { cn } from '@/lib/utils';
import type { TeamMember } from '@/lib/queries';

export interface AssigneeFilterProps {
  /** All slack_user_ids that have at least one ticket in the current group, with their counts. */
  assigneeCounts: Map<string, number>;
  /** Lookup so we can render avatars + names. */
  teamMembersBySlackId: Map<string, TeamMember>;
  /** Currently selected slack_user_ids (multi-select). Empty array = no filter. */
  selected: string[];
  /** Replace selection with this new array. */
  onChange: (next: string[]) => void;
  /** The signed-in user's slack_user_id, used to label the "Me" row. */
  mySlackId: string | null;
}

const SEARCH_THRESHOLD = 8;

export function AssigneeFilter({
  assigneeCounts,
  teamMembersBySlackId,
  selected,
  onChange,
  mySlackId,
}: AssigneeFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Sorted list of pickable assignees: highest count first, with the
  // current user pinned to the top so "Me + others" is one easy click.
  const rows = useMemo(() => {
    const entries = Array.from(assigneeCounts.entries()).map(([slackId, count]) => {
      const tm = teamMembersBySlackId.get(slackId);
      return {
        slackId,
        count,
        name: tm?.name ?? slackId,
        isMe: slackId === mySlackId,
      };
    });
    entries.sort((a, b) => {
      if (a.isMe !== b.isMe) return a.isMe ? -1 : 1;
      return b.count - a.count;
    });
    return entries;
  }, [assigneeCounts, teamMembersBySlackId, mySlackId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.name.toLowerCase().includes(q) || r.slackId.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const selectedSet = new Set(selected);
  const showSearch = rows.length >= SEARCH_THRESHOLD;

  // Smart trigger label.
  // - 0 selected → "Assignee · All"
  // - 1–2 selected → list names
  // - 3+ selected → "N selected"
  let triggerLabel = 'All';
  if (selected.length === 1) {
    const tm = teamMembersBySlackId.get(selected[0]);
    triggerLabel = tm?.name ?? 'Selected';
  } else if (selected.length === 2) {
    const names = selected
      .map((id) => teamMembersBySlackId.get(id)?.name ?? id)
      .join(', ');
    triggerLabel = names;
  } else if (selected.length > 2) {
    triggerLabel = `${selected.length} selected`;
  }

  const isActive = selected.length > 0;

  function toggle(slackId: string) {
    const next = new Set(selected);
    if (next.has(slackId)) next.delete(slackId);
    else next.add(slackId);
    onChange(Array.from(next));
  }

  function selectOnly(slackId: string) {
    onChange([slackId]);
  }

  function clear() {
    onChange([]);
  }

  function selectAllVisible() {
    onChange(filtered.map((r) => r.slackId));
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-full border pl-2 pr-2 text-xs font-medium transition-colors',
            isActive
              ? 'border-transparent bg-foreground text-background'
              : 'border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
          title="Filter by assignee"
        >
          <Users className="h-3 w-3" />
          <span className="max-w-[14rem] truncate">{triggerLabel}</span>
          {isActive ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clear();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  clear();
                }
              }}
              className="cursor-pointer rounded-full p-0.5 text-background/70 hover:bg-background/15 hover:text-background"
              aria-label="Clear assignee filter"
            >
              <X className="h-3 w-3" />
            </span>
          ) : (
            <ChevronDown className="h-3 w-3 opacity-60" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 p-0">
        {showSearch ? (
          <div className="border-b border-border/60 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search teammates…"
                className="h-7 pl-7 text-xs"
                autoFocus
              />
            </div>
          </div>
        ) : null}

        <div className="max-h-[320px] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-muted-foreground">
              {search.trim() ? 'No matches.' : 'No assigned tickets in this view.'}
            </p>
          ) : (
            filtered.map((r) => {
              const checked = selectedSet.has(r.slackId);
              return (
                <div
                  key={r.slackId}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggle(r.slackId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(r.slackId);
                    }
                  }}
                  className={cn(
                    'group flex w-full cursor-pointer items-center gap-2 px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent focus:bg-accent',
                  )}
                >
                  {/* Custom checkbox so we don't have to add a CheckboxItem primitive */}
                  <span
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                      checked
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-background',
                    )}
                  >
                    {checked ? <Check className="h-3 w-3" /> : null}
                  </span>
                  <UserAvatar
                    slackId={r.slackId}
                    teamMembersBySlackId={teamMembersBySlackId}
                    size="sm"
                  />
                  <span className="min-w-0 flex-1 truncate">
                    {r.name}
                    {r.isMe ? (
                      <span className="ml-1 text-3xs uppercase tracking-wide text-muted-foreground">
                        you
                      </span>
                    ) : null}
                  </span>
                  {/* "Only" — quick way to scope to just this person without un-checking the others first */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      selectOnly(r.slackId);
                    }}
                    className="invisible rounded px-1 py-0.5 text-3xs font-medium text-muted-foreground hover:bg-background hover:text-foreground group-hover:visible"
                  >
                    only
                  </button>
                  <span className="shrink-0 rounded bg-muted px-1 text-3xs tabular-nums text-muted-foreground">
                    {r.count}
                  </span>
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border/60 p-1.5">
          <button
            type="button"
            onClick={clear}
            disabled={!isActive}
            className="rounded px-2 py-1 text-2xs text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent"
          >
            Clear
          </button>
          {filtered.length > 1 ? (
            <button
              type="button"
              onClick={selectAllVisible}
              className="rounded px-2 py-1 text-2xs text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              Select all{search.trim() ? ' visible' : ''}
            </button>
          ) : null}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
